import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled/macro';
import Stack from '@mui/material/Stack';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import Button from 'components/Button';
import EmptyState from 'components/EmptyState/EmptyState';
import Paper from 'components/Paper/Paper';
import PaperContent from 'components/Paper/PaperContent';
import { format } from 'services/date-fns';
import { authActions, authSelectors } from 'redux/auth';

// Sits into PaperContent's own bottom padding, which is sized for prose
const Footer = styled(Stack)`
  margin-bottom: -0.75rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const CheckedAt = styled('div')`
  font-size: 0.8rem;
  color: #888;
  white-space: nowrap;
`;

const POLL_EVERY = 60 * 1000;

const PendingApproval = (args) => {

  const { t } = useTranslation(['component']);

  const dispatch = useDispatch();

  const workspace = useSelector(authSelectors.getWorkspace);

  const [checkedAt, setCheckedAt] = useState(null);

  const check = useCallback(() => {
    dispatch(authActions.getMember());
    setCheckedAt(new Date());
  }, [dispatch]);

  // An approval happens elsewhere and nothing pushes it here, so the page asks.
  // Once on arrival too, since it may have been granted while this was closed.
  useEffect(() => {
    check();

    const poll = setInterval(check, POLL_EVERY);

    return () => clearInterval(poll);
  }, [check]);

  // The grid item's own padding has to land on a wrapper, or it insets the card's
  // contents while the card itself still starts at the container's negative margin
  return (
    <div {...args}>
      <Paper>
        <PaperContent>
          <EmptyState
            title={t('pendingApproval.greeting')}
            subtitle={t('pendingApproval.info', { workspace: workspace?.name })}
          />

          <Footer direction="row" alignItems="center" justifyContent="space-between">
            <Button
              variant="text"
              size="small"
              startIcon={<RefreshRoundedIcon />}
              onClick={check}
              nowrap
            >
              {t('pendingApproval.refreshButton')}
            </Button>

            {checkedAt && (
              <CheckedAt>
                {t('pendingApproval.lastChecked', { time: format(checkedAt, 'p') })}
              </CheckedAt>
            )}
          </Footer>
        </PaperContent>
      </Paper>
    </div>
  );
};

export default PendingApproval;
